/**
 * IndexedDB persistence for search index
 * Stores the inverted index for fast reload on return visits
 */

import type { InvertedIndex } from './index';
import { serializeIndex, deserializeIndex } from './index';

const DB_NAME = 'solem-search';
const DB_VERSION = 1;
const STORE_NAME = 'searchIndex';

// Cache TTL - index is valid for 1 hour
const INDEX_TTL = 60 * 60 * 1000;

export interface StoredIndex {
  userId: string;
  indexData: string; // Serialized InvertedIndex
  docCount: number;
  timestamp: number;
}

/**
 * Open/create the search index database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });
}

/**
 * Save search index to IndexedDB
 */
export async function saveSearchIndex(
  userId: string,
  index: InvertedIndex
): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      
      const data: StoredIndex = {
        userId,
        indexData: serializeIndex(index),
        docCount: index.totalDocs,
        timestamp: Date.now(),
      };
      
      const request = store.put(data);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('Failed to save search index:', e);
  }
}

/**
 * Load search index from IndexedDB
 * Returns null if index doesn't exist or is stale
 */
export async function loadSearchIndex(userId: string): Promise<InvertedIndex | null> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(userId);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const data = request.result as StoredIndex | undefined;
        
        if (!data) {
          resolve(null);
          return;
        }
        
        // Check TTL
        if (Date.now() - data.timestamp > INDEX_TTL) {
          resolve(null);
          return;
        }
        
        // Deserialize the index
        const index = deserializeIndex(data.indexData);
        resolve(index);
      };
    });
  } catch (e) {
    console.warn('Failed to load search index:', e);
    return null;
  }
}

/**
 * Check if stored index is valid for current data
 * Compares document count to detect if rebuild is needed
 */
export async function isIndexValid(
  userId: string,
  currentDocCount: number
): Promise<boolean> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(userId);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const data = request.result as StoredIndex | undefined;
        
        if (!data) {
          resolve(false);
          return;
        }
        
        // Check TTL
        if (Date.now() - data.timestamp > INDEX_TTL) {
          resolve(false);
          return;
        }
        
        // Check document count matches
        resolve(data.docCount === currentDocCount);
      };
    });
  } catch (e) {
    console.warn('Failed to check index validity:', e);
    return false;
  }
}

/**
 * Clear search index for a user
 */
export async function clearSearchIndex(userId: string): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(userId);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('Failed to clear search index:', e);
  }
}

/**
 * Clear all search indexes
 */
export async function clearAllSearchIndexes(): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('Failed to clear all search indexes:', e);
  }
}

